/**
 * Seeds Actions Coordinator Hook
 *
 * Composes modal state and action handlers into a unified interface.
 * This is the main hook used by components for bulk seed operations.
 *
 * Provides:
 * - Modal state and handlers (comment, tag, remove tag, export, confirmation, addSeed)
 * - Action handlers (comment, status changes, scans, share, export)
 * - Selected row count for UI feedback
 *
 * Pattern: Three-layer composition (matches Technology section)
 * Risk Level: Low
 * - Pure composition of other hooks
 * - No side effects
 * - Clear dependencies
 */

import type { Seed, SeedStatus } from '@/types';

import {
  SeedsActionHandlers,
  useSeedsActionHandlers,
} from './actions/useSeedsActionHandlers';
import { SeedModalState, useSeedModal } from './useSeedModal';

/**
 * Parameters for useSeedsActions hook
 */
export interface UseSeedsActionsProps {
  /** Currently selected seeds */
  selectedSeeds: Seed[];
  /** Number of selected rows (for toast messages and UI) */
  selectedRowsCount: number;
  /** Function to invalidate/refetch seed data */
  invalidateSeeds: () => void;
  /** Function to set confirm status modal state */
  setConfirmStatus: (status: unknown) => void;
}

/**
 * Modal handlers interface (extracted from modal state for cleaner API)
 */
export interface SeedModalHandlers {
  /** Open comment modal */
  openCommentModal: () => void;
  /** Close comment modal */
  closeCommentModal: () => void;
  /** Open tag modal */
  openTagModal: () => void;
  /** Close tag modal */
  closeTagModal: () => void;
  /** Open remove tag modal */
  openRemoveTagModal: () => void;
  /** Close remove tag modal */
  closeRemoveTagModal: () => void;
  /** Open export modal */
  openExportModal: () => void;
  /** Close export modal */
  closeExportModal: () => void;
  /** Open import modal */
  openImportModal: () => void;
  /** Close import modal */
  closeImportModal: () => void;
  /** Open add seed modal */
  openAddSeedModal: () => void;
  /** Close add seed modal */
  closeAddSeedModal: () => void;
  /** Open confirmation modal with status and seeds */
  openConfirmationModal: (status: SeedStatus, seeds: Seed[]) => void;
  /** Close confirmation modal */
  closeConfirmationModal: () => void;
}

/**
 * Return type for useSeedsActions hook
 */
export interface UseSeedsActionsReturn {
  /** Current state of all modals */
  modalState: SeedModalState;
  /** Handlers for opening and closing modals */
  modalHandlers: SeedModalHandlers;
  /** Action handlers for bulk operations */
  handlers: SeedsActionHandlers;
  /** Number of selected rows */
  selectedRowsCount: number;
}

/**
 * Custom hook for coordinating seed actions
 *
 * Combines modal state management and action handlers into a single interface.
 * This is the main hook used by components that need to perform bulk operations.
 *
 * Example usage:
 * ```tsx
 * const { modalState, modalHandlers, handlers, selectedRowsCount } = useSeedsActions({
 *   selectedSeeds,
 *   selectedRowsCount: selectedKeys.length,
 *   invalidateSeeds,
 *   setConfirmStatus,
 * });
 *
 * // Use in action toolbar
 * <Button onClick={modalHandlers.openCommentModal}>Add Comment</Button>
 *
 * // Use in modals
 * <NoteModal
 *   open={modalState.comment.isOpen}
 *   onClose={modalHandlers.closeCommentModal}
 *   onSubmit={handlers.handleComment}
 * />
 *
 * // Use in export modal
 * <UniversalExportModal
 *   open={modalState.export.isOpen}
 *   onClose={modalHandlers.closeExportModal}
 *   selectedRows={selectedSeeds.map(s => s.key)}
 * />
 * ```
 *
 * @param props - Configuration object with dependencies
 * @returns Object containing modal state, handlers, and action handlers
 */
export const useSeedsActions = ({
  selectedSeeds,
  selectedRowsCount,
  invalidateSeeds,
  setConfirmStatus,
}: UseSeedsActionsProps): UseSeedsActionsReturn => {
  // Layer 1: Modal state management
  const modalState = useSeedModal();

  // Layer 2: Action handlers (business logic)
  const handlers = useSeedsActionHandlers({
    selectedSeeds,
    invalidateSeeds,
    setConfirmStatus,
  });

  // Extract modal handlers for cleaner interface
  const modalHandlers: SeedModalHandlers = {
    openCommentModal: modalState.comment.open,
    closeCommentModal: modalState.comment.close,
    openTagModal: modalState.tag.open,
    closeTagModal: modalState.tag.close,
    openRemoveTagModal: modalState.removeTag.open,
    closeRemoveTagModal: modalState.removeTag.close,
    openExportModal: modalState.export.open,
    closeExportModal: modalState.export.close,
    openImportModal: modalState.import.open,
    closeImportModal: modalState.import.close,
    openAddSeedModal: modalState.addSeed.open,
    closeAddSeedModal: modalState.addSeed.close,
    openConfirmationModal: modalState.confirmation.open,
    closeConfirmationModal: modalState.confirmation.close,
  };

  return {
    modalState,
    modalHandlers,
    handlers,
    selectedRowsCount,
  };
};
